import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      toast.success('Welcome to vWatch Admin!');
      navigate('/dashboard');
    } else {
      toast.error(result.error || 'Login failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 relative">
      <div className="absolute inset-0 opacity-5"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1611353229530-f17036b8479a?crop=entropy&cs=srgb&fm=jpg&q=85)', backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(10px)' }} />
      
      <Card className="relative w-full max-w-md bg-card/80 border border-border rounded-xl shadow-[0_0_0_1px_rgba(208,179,107,0.10)]">
        <CardHeader className="text-center pt-6 pb-2">
          <div className="mx-auto">
            <img src="/vwatch-logo.png" alt="vWatch Admin" className="h-16 mx-auto" />
          </div>
          <CardTitle className="text-2xl mt-2">Welcome Back</CardTitle>
          <CardDescription>Sign in to manage your luxury watch inventory</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
              <Input id="email" type="email" placeholder="your@email.com" value={email}
                onChange={(e) => setEmail(e.target.value)} data-testid="email-input"
                className="bg-secondary border-input focus-visible:ring-2 focus-visible:ring-[hsl(42,44%,58%)]" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">Password</Label>
              <div className="relative">
                <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Enter password" value={password}
                  onChange={(e) => setPassword(e.target.value)} data-testid="password-input"
                  className="bg-secondary border-input focus-visible:ring-2 focus-visible:ring-[hsl(42,44%,58%)] pr-10" required />
                <button type="button" onClick={() => setShowPassword(p => !p)} tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="toggle-password">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" data-testid="login-submit-button" disabled={loading}
              className="w-full h-11 rounded-md bg-primary text-primary-foreground hover:bg-[hsl(42,44%,62%)] shadow-[0_0_0_1px_rgba(208,179,107,0.18),_0_6px_20px_rgba(0,0,0,0.35)]">
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
